import React from 'react';
import {
  Banner,
  Divider,
  ExternalLink,
  Flex,
  Gap,
  PostMessage,
  Tabs,
  Tag,
  Text,
  Table,
  Col,
  Row,
} from '@common/components';
import { capitalize } from 'lodash';
import { formatPhone } from 'works/legacy/services/phone-service';
import { SuppliersManagementViewDrawerInfoRow } from './_suppliers-management-view-drawer-info-row';
import { SuppliersManagementViewDrawerFacilities } from './_suppliers-management-view-drawer-facilities';
import { SupplierStatus } from '@institutions/components';
import { SuppliersManagementViewDrawerInvitationMessage } from './_suppliers-management-view-drawer-invitation-message';
import { SuppliersManagementViewDrawerTierSection } from './_suppliers-management-view-drawer-tier-section';
import { SuppliersManagementFilesDrawer } from '@institutions/pages/organization/suppliers-management';
import { FormInstance } from 'antd/es/form/Form';
import { SuppliersManagementViewDrawerHistory } from './_suppliers-management-view-drawer-history';
import { SuppliersManagementViewDrawerRolesAccess } from './_suppliers-management-view-drawer-roles-access';
import { StoreValue } from '@common/hooks';
import { useCurrentMembership } from '@identity/hooks';
import { SupplierQueryType } from '@institutions/graphql';
import { FontSize } from '@common/tokens';

const tableColumns = (status?: boolean) => {
  const columns = [
    {
      key: 'contactType',
      dataIndex: ['contactType'],
      title: 'Type',
      render: (value: string) => <>{capitalize(value)}</>,
    },
    {
      key: 'contactName',
      dataIndex: ['worksMembership', 'name'],
      title: 'Name',
    },
    {
      key: 'contactEmail',
      dataIndex: ['worksMembership', 'email'],
      title: 'E-mail',
    },
    {
      key: 'contactPhone',
      dataIndex: ['worksMembership', 'phone'],
      title: 'Phone Number',
      render: formatPhone,
    },
    {
      key: 'contactTimezone',
      dataIndex: ['worksMembership', 'timeZoneAbbreviation'],
      title: 'Time Zone',
    },
  ];

  if (status) {
    columns.push({
      key: 'status',
      dataIndex: ['worksMembership', 'status'],
      title: 'Status',
      render: (value: string) => (
        <Tag color="steelLighter">
          <Text size="sm">{capitalize(value)}</Text>
        </Tag>
      ),
    });
  }

  return columns;
};

export const tabKeys = {
  details: 'details',
  contacts: 'contacts',
  locations: 'locations',
  rolesAccess: 'rolesAccess',
  history: 'history',
  files: 'files',
};

export type TabKeysType = keyof typeof tabKeys;

type Props = {
  form: FormInstance;
  onFormSubmit: (value: Record<string, StoreValue>) => void;
  onTabChange: (value: TabKeysType) => void;
  tierEditionState: [boolean, (v: boolean) => void];
  filesEditionMode: boolean;
  rolesAccessEditionMode: boolean;
  supplier: SupplierQueryType;
};

export function SuppliersManagementViewDrawerContent({
  form,
  onFormSubmit,
  onTabChange,
  tierEditionState,
  filesEditionMode,
  rolesAccessEditionMode,
  supplier: {
    id,
    phoneNumber,
    website,
    address,
    city,
    stateCode,
    postalCode,
    defaultContact,
    organizationBusinesses,
    organizationUnitsActive,
    contacts,
    statusForCurrentOrganization,
    activationLogForCurrentOrganization: activationLog,
    supplierSignupInvitation,
  },
}: Props) {
  const currentMembership = useCurrentMembership();
  const canUserSeeTiering = currentMembership.canShowTiering;
  const status = statusForCurrentOrganization?.status;

  const isStatusPendingSignup = status === 'PENDING_SIGNUP';

  const allContacts = defaultContact
    ? [{ contactType: 'MAIN', worksMembership: defaultContact }, ...contacts]
    : isStatusPendingSignup
      ? [
          {
            contactType: 'MAIN',
            worksMembership: {
              id: 'invited',
              name: '',
              email: supplierSignupInvitation?.email || '',
              phone: '',
              status: 'pending',
            },
          },
          ...contacts,
        ]
      : contacts;

  const statusTooltip =
    status === 'PENDING_ACTIVATION'
      ? 'No locations/units permissions mapped yet.'
      : status === 'PENDING_SIGNUP'
        ? 'The invite was sent successfully, but the signup is still pending.'
        : '';

  const isStatusActive = status === 'ACTIVE';
  const formattedAddress = `${address}, ${city}, ${stateCode} - ${postalCode}`;
  const formattedPhoneNumber =
    (phoneNumber && formatPhone(phoneNumber)) || 'N/A';

  const comments = supplierSignupInvitation?.comments;
  const inviteNote = comments?.length ? comments[0] : null;

  return (
    <>
      <div className="supplier-view-drawer__container">
        <Col span={24}>
          <Tabs onChange={(v) => onTabChange(v as TabKeysType)}>
            <Tabs.Pane key={tabKeys.details} tab="Details">
              <Gap vertical align="stretch" size="lg">
                {isStatusPendingSignup && (
                  <Banner type="info" style={{ marginTop: 5, marginBottom: 8 }}>
                    <Text>
                      Waiting for supplier to accept the invite and create an
                      account. Follow up if needed.
                    </Text>
                  </Banner>
                )}

                <Row gutter={[0, 12]}>
                  <SuppliersManagementViewDrawerInfoRow
                    label="Status"
                    info={<SupplierStatus status={status} />}
                    tooltip={statusTooltip}
                  />
                  {!isStatusPendingSignup && (
                    <>
                      <SuppliersManagementViewDrawerInfoRow
                        label="Address"
                        info={formattedAddress}
                      />
                      <SuppliersManagementViewDrawerInfoRow
                        label="Phone"
                        info={formattedPhoneNumber}
                      />
                      <SuppliersManagementViewDrawerInfoRow
                        label="Website"
                        info={
                          website ? <ExternalLink url={website} /> : <>N/A</>
                        }
                      />
                      {activationLog && status && (
                        <SuppliersManagementViewDrawerInvitationMessage
                          status={status}
                          activationLog={activationLog}
                        />
                      )}
                    </>
                  )}
                </Row>

                {isStatusPendingSignup && inviteNote && (
                  <Flex vertical align="stretch">
                    <Text>Invite Note</Text>
                    <PostMessage
                      className="organization-invitation-message"
                      message={inviteNote.contentAsText}
                      name={inviteNote.author.name || ''}
                      time={inviteNote.createdAt || ''}
                    />
                  </Flex>
                )}

                {(isStatusActive || isStatusPendingSignup) &&
                  canUserSeeTiering && (
                    <>
                      <Divider />
                      <SuppliersManagementViewDrawerTierSection
                        form={form}
                        supplierId={id}
                        onFormSubmit={onFormSubmit}
                        tierEditionState={tierEditionState}
                      />
                    </>
                  )}
              </Gap>
            </Tabs.Pane>
            <Tabs.Pane key={tabKeys.contacts} tab="Contacts">
              <Table
                className="admin-supplier-view-drawer__contact-list"
                rowKey="id"
                columns={tableColumns(isStatusPendingSignup)}
                dataSource={allContacts}
                pagination={false}
              />
            </Tabs.Pane>
            {isStatusActive && (
              <Tabs.Pane key={tabKeys.locations} tab="Locations">
                <SuppliersManagementViewDrawerFacilities
                  organizationBusinesses={organizationBusinesses}
                  organizationUnits={organizationUnitsActive}
                />
              </Tabs.Pane>
            )}
            {isStatusActive && (
              <Tabs.Pane key={tabKeys.rolesAccess} tab="Roles Access">
                <SuppliersManagementViewDrawerRolesAccess
                  supplierId={id}
                  form={form}
                  onFormSubmit={onFormSubmit}
                  rolesAccessEditionMode={rolesAccessEditionMode}
                />
              </Tabs.Pane>
            )}
            <Tabs.Pane key={tabKeys.files} tab="Files">
              <SuppliersManagementFilesDrawer
                supplierId={id}
                form={form}
                onFormSubmit={onFormSubmit}
                filesEditionMode={filesEditionMode}
              />
            </Tabs.Pane>
            <Tabs.Pane key={tabKeys.history} tab="History">
              <SuppliersManagementViewDrawerHistory supplierId={id} />
            </Tabs.Pane>
          </Tabs>
        </Col>
      </div>
      <style jsx>{`
        .supplier-view-drawer__container {
          font-size: ${FontSize.md};
        }
        .supplier-view-drawer__container :global(.ant-tabs-content) {
          margin-top: 32px;
        }
      `}</style>
    </>
  );
}
